import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Search, FileText, Send, CheckCircle2, XCircle,
  ChevronLeft, ChevronRight, Mail, Eye, Download,
  AlertTriangle, Clock,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { invoicesAPI, customersAPI } from '../lib/api';
import InvoiceDetailSheet from '../components/InvoiceDetailSheet';
import { formatCurrency, formatDate } from '../lib/utils';
import { toast } from 'sonner';

const hFont = { fontFamily: 'Manrope, sans-serif' };

const STATUS_COLORS = {
  draft: 'bg-stone-100 text-stone-700',
  sent: 'bg-blue-50 text-blue-700 border-blue-200',
  paid: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  overdue: 'bg-amber-50 text-amber-700 border-amber-200',
  cancelled: 'bg-red-50 text-red-700 border-red-200',
};

const REMINDER_LABELS = { 1: 'First Reminder', 2: 'Second Reminder', 3: 'Final Reminder' };

// ─── Inline customer creation form ──────────────────
function InlineCustomerForm({ onCreated, onCancel }) {
  const [form, setForm] = useState({ name: '', address_line1: '', postal_code: '', city: '', country_code: 'CH', email: '' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!form.name.trim()) return toast.error('Customer name is required');
    setSaving(true);
    try {
      const res = await customersAPI.create(form);
      toast.success('Customer created');
      onCreated(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  return (
    <div className="border border-stone-200 rounded-lg p-4 bg-stone-50 space-y-3" data-testid="inline-customer-form">
      <p className="text-xs font-bold text-stone-700" style={hFont}>Quick-add Customer</p>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <Label className="text-xs text-stone-600">Name *</Label>
          <Input value={form.name} onChange={e => set('name', e.target.value)} className="h-8 text-sm" data-testid="quick-customer-name" />
        </div>
        <div className="col-span-2">
          <Label className="text-xs text-stone-600">Address</Label>
          <Input value={form.address_line1} onChange={e => set('address_line1', e.target.value)} className="h-8 text-sm" />
        </div>
        <div>
          <Label className="text-xs text-stone-600">Postal Code</Label>
          <Input value={form.postal_code} onChange={e => set('postal_code', e.target.value)} className="h-8 text-sm" />
        </div>
        <div>
          <Label className="text-xs text-stone-600">City</Label>
          <Input value={form.city} onChange={e => set('city', e.target.value)} className="h-8 text-sm" />
        </div>
        <div className="col-span-2">
          <Label className="text-xs text-stone-600">Email</Label>
          <Input type="email" value={form.email} onChange={e => set('email', e.target.value)} className="h-8 text-sm" data-testid="quick-customer-email" />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={onCancel}>Cancel</Button>
        <Button size="sm" className="h-7 text-xs bg-stone-900 hover:bg-stone-800 text-white" onClick={submit} disabled={saving} data-testid="quick-customer-save">
          {saving ? 'Saving...' : 'Add Customer'}
        </Button>
      </div>
    </div>
  );
}


// ─── Main Page ──────────────────────────────────────
export default function InvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');

  // Create invoice state
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [customerId, setCustomerId] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  let _invLineId = 0;
  const _newInvLine = (overrides = {}) => ({ _uid: `inv-${++_invLineId}-${Date.now()}`, description: '', quantity: 1, unit_price: '', vat_rate: '', ...overrides });
  const [lineItems, setLineItems] = useState([_newInvLine()]);
  const [showQuickCustomer, setShowQuickCustomer] = useState(false);

  // View invoice
  const [viewInvoice, setViewInvoice] = useState(null);

  const fetchInvoices = useCallback(async (p = page) => {
    setLoading(true);
    try {
      const params = { page: p, page_size: 50 };
      if (statusFilter) params.status = statusFilter;
      const res = await invoicesAPI.getAll(params);
      setInvoices(res.data.items || []);
      setTotalPages(res.data.pages || 1);
    } catch { setInvoices([]); }
    setLoading(false);
  }, [page, statusFilter]);

  const fetchCustomers = useCallback(async () => {
    try {
      const res = await customersAPI.getAll();
      setCustomers(res.data.items || []);
    } catch {}
  }, []);

  useEffect(() => { fetchInvoices(page); }, [fetchInvoices, page]);
  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  // Default due date = invoice date + 30 days
  useEffect(() => {
    if (invoiceDate && !dueDate) {
      const d = new Date(invoiceDate);
      d.setDate(d.getDate() + 30);
      setDueDate(d.toISOString().slice(0, 10));
    }
  }, [invoiceDate, dueDate]);

  const updateLine = (i, field, val) => {
    const next = [...lineItems];
    next[i] = { ...next[i], [field]: val };
    setLineItems(next);
  };

  const addLineItem = () => setLineItems([...lineItems, _newInvLine()]);
  const removeLineItem = (i) => { if (lineItems.length > 1) setLineItems(lineItems.filter((_, j) => j !== i)); };

  const subtotal = lineItems.reduce((s, l) => s + (parseFloat(l.quantity) || 0) * (parseFloat(l.unit_price) || 0), 0);
  const totalVat = lineItems.reduce((s, l) => {
    const net = (parseFloat(l.quantity) || 0) * (parseFloat(l.unit_price) || 0);
    return s + net * (parseFloat(l.vat_rate) || 0) / 100;
  }, 0);

  const handleCreateInvoice = async () => {
    if (!customerId) return toast.error('Select a customer');
    if (lineItems.some(l => !l.description || !l.unit_price)) return toast.error('All line items need a description and price');
    setCreating(true);
    try {
      await invoicesAPI.create({
        customer_id: customerId,
        invoice_date: invoiceDate,
        due_date: dueDate,
        lines: lineItems.map(l => ({
          description: l.description,
          quantity: parseFloat(l.quantity) || 1,
          unit_price: parseFloat(l.unit_price) || 0,
          vat_rate: parseFloat(l.vat_rate) || 0,
        })),
        notes,
      });
      toast.success('Invoice created');
      setShowCreate(false);
      resetForm();
      fetchInvoices(1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create invoice');
    }
    setCreating(false);
  };

  const resetForm = () => {
    setCustomerId('');
    setInvoiceDate(new Date().toISOString().slice(0, 10));
    setDueDate('');
    setNotes('');
    setLineItems([_newInvLine()]);
    setShowQuickCustomer(false);
  };

  const handleAction = async (action, invoice) => {
    if (action === 'refresh') {
      fetchInvoices(page);
      // Refresh the viewed invoice if it's the same
      if (viewInvoice) {
        try {
          const res = await invoicesAPI.getById(viewInvoice.id);
          setViewInvoice(res.data);
        } catch { setViewInvoice(null); }
      }
      return;
    }
    try {
      if (action === 'post') await invoicesAPI.post(invoice.id);
      else if (action === 'paid') await invoicesAPI.markPaid(invoice.id);
      else if (action === 'cancel') await invoicesAPI.cancel(invoice.id);
      else if (action === 'send') await invoicesAPI.send(invoice.id);
      toast.success(
        action === 'post' ? 'Invoice posted to GL' :
        action === 'paid' ? 'Invoice marked as paid' :
        action === 'cancel' ? 'Invoice cancelled' :
        'Invoice sent'
      );
      fetchInvoices(page);
      setViewInvoice(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action} invoice`);
    }
  };

  const onCustomerCreated = (newCustomer) => {
    setCustomers(prev => [...prev, newCustomer].sort((a, b) => a.name.localeCompare(b.name)));
    setCustomerId(newCustomer.id);
    setShowQuickCustomer(false);
  };

  const filtered = invoices.filter(inv => {
    if (!search) return true;
    const q = search.toLowerCase();
    return inv.customer_name?.toLowerCase().includes(q) || inv.invoice_number?.includes(q);
  });

  return (
    <div className="space-y-6" data-testid="invoices-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-stone-900" style={hFont}>Invoices</h2>
          <p className="text-sm text-stone-500">Create, send, and track customer invoices</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-stone-900 hover:bg-stone-800 text-white h-9 px-5 text-sm font-semibold rounded-full" data-testid="new-invoice-btn">
          <Plus className="w-4 h-4 mr-1.5" /> New Invoice
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative max-w-xs flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by number or customer..." className="pl-9 h-9 text-sm" data-testid="invoice-search" />
        </div>
        <Select value={statusFilter} onValueChange={v => { setStatusFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-36 h-9 text-sm" data-testid="invoice-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="overdue">Overdue</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Invoice list */}
      {loading ? (
        <div className="text-center py-12 text-sm text-stone-500">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <FileText className="w-10 h-10 text-stone-300 mx-auto mb-3" />
          <p className="text-sm text-stone-500">No invoices yet. Create your first invoice to get started.</p>
        </div>
      ) : (
        <div className="border border-stone-200 rounded-xl overflow-hidden bg-white">
          {/* Desktop table */}
          <table className="w-full text-sm hidden md:table" data-testid="invoice-table">
            <thead>
              <tr className="bg-stone-50 border-b border-stone-200">
                <th className="text-left text-xs font-semibold text-stone-500 px-4 py-2.5">Number</th>
                <th className="text-left text-xs font-semibold text-stone-500 px-4 py-2.5">Customer</th>
                <th className="text-left text-xs font-semibold text-stone-500 px-4 py-2.5">Date</th>
                <th className="text-left text-xs font-semibold text-stone-500 px-4 py-2.5">Due</th>
                <th className="text-right text-xs font-semibold text-stone-500 px-4 py-2.5">Total</th>
                <th className="text-center text-xs font-semibold text-stone-500 px-4 py-2.5">Status</th>
                <th className="text-right text-xs font-semibold text-stone-500 px-4 py-2.5 w-28">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(inv => (
                <tr key={inv.id} className="border-b border-stone-100 last:border-0 hover:bg-stone-50/60 transition-colors cursor-pointer" onClick={() => setViewInvoice(inv)} data-testid={`invoice-row-${inv.invoice_number}`}>
                  <td className="px-4 py-3 font-mono font-bold text-stone-900">{inv.invoice_number}</td>
                  <td className="px-4 py-3 text-stone-800">{inv.customer_name}</td>
                  <td className="px-4 py-3 text-stone-600">{formatDate(inv.invoice_date)}</td>
                  <td className="px-4 py-3 text-stone-600">{formatDate(inv.due_date)}</td>
                  <td className="px-4 py-3 text-right font-mono font-bold text-stone-900">{formatCurrency(inv.total)}</td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <Badge variant="outline" className={`text-[10px] ${STATUS_COLORS[inv.status] || ''}`}>{inv.status}</Badge>
                      {inv.reminder_level > 0 && (
                        <span className="text-[9px] text-amber-600 font-medium">R{inv.reminder_level}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setViewInvoice(inv)} data-testid={`view-invoice-${inv.invoice_number}`}>
                        <Eye className="w-3.5 h-3.5" />
                      </Button>
                      {inv.status === 'draft' && (
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-stone-900" onClick={() => handleAction('post', inv)} data-testid={`post-invoice-${inv.invoice_number}`}>
                          <Send className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      {inv.status === 'sent' && (
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-emerald-600" onClick={() => handleAction('paid', inv)} data-testid={`paid-invoice-${inv.invoice_number}`}>
                          <CheckCircle2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Mobile card list */}
          <div className="md:hidden divide-y divide-stone-100">
            {filtered.map(inv => (
              <button
                key={inv.id}
                className="w-full text-left px-4 py-3.5 flex items-center gap-3 active:bg-stone-50 transition-colors"
                onClick={() => setViewInvoice(inv)}
                data-testid={`invoice-row-${inv.invoice_number}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="font-mono font-bold text-sm text-stone-900">{inv.invoice_number}</span>
                    <Badge variant="outline" className={`text-[9px] px-1.5 py-0 ${STATUS_COLORS[inv.status] || ''}`}>{inv.status}</Badge>
                    {inv.reminder_level > 0 && <span className="text-[9px] text-amber-600 font-medium">R{inv.reminder_level}</span>}
                  </div>
                  <p className="text-xs text-stone-600 truncate">{inv.customer_name}</p>
                  <p className="text-[10px] text-stone-400 mt-0.5">Due {formatDate(inv.due_date)}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="font-mono font-bold text-sm text-stone-900">{formatCurrency(inv.total)}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-stone-300 flex-shrink-0" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-stone-500">Page {page} of {totalPages}</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="h-8"><ChevronLeft className="w-4 h-4" /></Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="h-8"><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>
      )}

      {/* ─── Create Invoice Dialog ──────────────────────── */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto w-[95vw] sm:w-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold" style={hFont}>New Invoice</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Customer selector + inline create */}
            {showQuickCustomer ? (
              <InlineCustomerForm
                onCreated={onCustomerCreated}
                onCancel={() => setShowQuickCustomer(false)}
              />
            ) : (
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Label className="text-xs font-semibold text-stone-600 mb-1.5">Customer</Label>
                  <Select value={customerId} onValueChange={setCustomerId}>
                    <SelectTrigger data-testid="invoice-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
                    <SelectContent>
                      {customers.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}{c.city ? ` — ${c.city}` : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={() => setShowQuickCustomer(true)} className="h-9 text-xs" data-testid="add-customer-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" /> New
                </Button>
              </div>
            )}

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold text-stone-600 mb-1.5">Invoice Date</Label>
                <Input type="date" value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)} data-testid="invoice-date" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-stone-600 mb-1.5">Due Date</Label>
                <Input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} data-testid="invoice-due-date" />
              </div>
            </div>

            {/* Line items */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-bold text-stone-900" style={hFont}>Line Items</Label>
                <Button type="button" variant="outline" size="sm" onClick={addLineItem} className="h-7 text-xs">
                  <Plus className="w-3 h-3 mr-1" /> Add Line
                </Button>
              </div>
              <div className="space-y-2">
                {lineItems.map((item, i) => (
                  <div key={item._uid} className="grid grid-cols-12 gap-2 items-center" data-testid={`inv-line-${i}`}>
                    <Input value={item.description} onChange={e => updateLine(i, 'description', e.target.value)} placeholder="Description" className="col-span-5 h-8 text-sm" />
                    <Input type="number" step="1" min="1" value={item.quantity} onChange={e => updateLine(i, 'quantity', e.target.value)} placeholder="Qty" className="col-span-2 h-8 text-sm" />
                    <Input type="number" step="0.01" min="0" value={item.unit_price} onChange={e => updateLine(i, 'unit_price', e.target.value)} placeholder="Price" className="col-span-2 h-8 text-sm" />
                    <Input type="number" step="0.1" min="0" value={item.vat_rate} onChange={e => updateLine(i, 'vat_rate', e.target.value)} placeholder="VAT%" className="col-span-2 h-8 text-sm" />
                    <button type="button" onClick={() => removeLineItem(i)} className="col-span-1 text-stone-400 hover:text-red-500 text-center">
                      <XCircle className="w-4 h-4 mx-auto" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="mt-3 text-right space-y-0.5">
                <p className="text-xs text-stone-500">Subtotal: <span className="font-mono font-bold text-stone-900">{formatCurrency(subtotal)}</span></p>
                <p className="text-xs text-stone-500">VAT: <span className="font-mono font-bold text-stone-900">{formatCurrency(totalVat)}</span></p>
                <p className="text-sm text-stone-900 font-bold" data-testid="invoice-total">Total: <span className="font-mono">{formatCurrency(subtotal + totalVat)}</span></p>
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold text-stone-600 mb-1.5">Notes (optional)</Label>
              <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Payment instructions, reference..." data-testid="invoice-notes" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreateInvoice} disabled={creating} className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="create-invoice-submit">
              {creating ? 'Creating...' : 'Create Invoice'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── View Invoice Sheet ────────────────────────── */}
      {viewInvoice && (
        <InvoiceDetailSheet
          invoice={viewInvoice}
          onAction={handleAction}
          onClose={() => setViewInvoice(null)}
        />
      )}
    </div>
  );
}
